import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolderPlus } from '@fortawesome/free-solid-svg-icons';
import { CreateCategoryBindingModel, VideoLibraryApiClient } from '../apiClient';
import { getErrorMessage, getFieldError } from '../helpers/errorHelper';

export type AddCategoryModalProps = {
    show: boolean;
    onHide: () => void;
    apiClient: VideoLibraryApiClient;
    onSuccess?: (categoryName: string) => void;
};

export const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
    show,
    onHide,
    apiClient,
    onSuccess,
}) => {
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [fieldError, setFieldError] = useState<string | null>(null);
    const [generalError, setGeneralError] = useState<string | null>(null);

    const handleClose = () => {
        if (!submitting) {
            setName('');
            setFieldError(null);
            setGeneralError(null);
            onHide();
        }
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        if (fieldError) {
            setFieldError(null);
        }
        if (generalError) {
            setGeneralError(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed || submitting) return;

        setSubmitting(true);
        setFieldError(null);
        setGeneralError(null);
        try {
            await apiClient.categoriesPOST(new CreateCategoryBindingModel({ name: trimmed }));
            onSuccess?.(trimmed);
            setName('');
            onHide();
        } catch (err: any) {
            console.error(err);
            const nameValidation = getFieldError(err, 'Name') ?? getFieldError(err, 'name');
            if (nameValidation) {
                setFieldError(nameValidation);
            } else {
                setGeneralError(getErrorMessage(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faFolderPlus} className="text-primary" />
                    <span>Add Category</span>
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Body>
                    {generalError && (
                        <Alert variant="danger" className="py-2 small">
                            {generalError}
                        </Alert>
                    )}
                    <Form.Group controlId="add-category-modal-name">
                        <Form.Label>Category name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g. Comedy, Science, Tutorials..."
                            value={name}
                            onChange={handleNameChange}
                            isInvalid={!!fieldError}
                            disabled={submitting}
                            autoFocus
                            required
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldError}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={!name.trim() || submitting}>
                        {submitting ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Adding...
                            </>
                        ) : (
                            'Add category'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};
